import i18n from "i18next";
import { useEffect, useState } from "react";
import { useTranslation as useTranslationOriginal } from "react-i18next";
import type { TFunction, TranslationInput, TranslationRecord } from "./types";

const syncLoaded = new WeakMap<TranslationInput, Set<string>>();
const asyncLoading = new WeakMap<
  TranslationInput,
  Map<string, Promise<void>>
>();
const namespaces = new WeakMap<TranslationInput, string>();
let nsCounter = 0;

function getNamespace(loaders: TranslationInput): string {
  let ns = namespaces.get(loaders);
  if (!ns) {
    ns = `ct-${nsCounter++}`;
    namespaces.set(loaders, ns);
  }
  return ns;
}

/**
 * Hook for accessing component-scoped translations.
 *
 * Always takes a translations loader object and returns a typed `t` function.
 * The loader must be defined as a module-level constant so the reference is stable.
 *
 * ```
 * import de from "./translations/de.json";
 *
 * const translations = {
 *   de,
 *   en: () => import("./translations/en.json"),
 * };
 *
 * // In component:
 * const t = useUntypedTranslation(translations);
 * t("myKey.subKey"); // fully typed autocomplete
 * ```
 *
 * Prefer using the typed re-export from your app's i18n module instead of this
 * function directly. The re-export fixes `SyncLanguages` (e.g. to `"de"`).
 */
export function useUntypedTranslation<T extends TranslationRecord>(
  translations: TranslationInput<T>
): TFunction<T> {
  const namespace = getNamespace(translations);

  // Register sync loaders eagerly so translations are available on first render.
  const syncLoadedLangs = syncLoaded.get(translations) ?? new Set();
  syncLoaded.set(translations, syncLoadedLangs);

  for (const [langKey, loader] of Object.entries(translations)) {
    if (typeof loader !== "function" && !syncLoadedLangs.has(langKey)) {
      syncLoadedLangs.add(langKey);
      i18n.addResourceBundle(langKey, namespace, loader, true, false);
    }
  }

  const { t, i18n: i18nInstance } = useTranslationOriginal(namespace);
  const lang = i18nInstance.language;
  const [, rerender] = useState(0);

  useEffect(() => {
    if (!i18nInstance.isInitialized) return;

    const promises =
      asyncLoading.get(translations) ?? new Map<string, Promise<void>>();
    asyncLoading.set(translations, promises);

    if (!promises.has(lang)) {
      const loader = translations[lang];
      if (loader && typeof loader === "function") {
        const promise = (
          loader as unknown as () => Promise<{ default: TranslationRecord }>
        )().then((mod) => {
          i18nInstance.addResourceBundle(
            lang,
            namespace,
            mod.default,
            true,
            false
          );
        });
        promises.set(lang, promise);
      }
    }

    const promise = promises.get(lang);
    if (!promise) return;

    let cancelled = false;
    promise.then(() => {
      if (!cancelled) {
        rerender((n) => n + 1);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [lang, i18nInstance, translations, namespace]);

  return t as unknown as TFunction<T>;
}
